import { Component, effect, inject, OnDestroy } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormInputComponent, FormMultiSelectComponent } from '@shared/forms';
import { MediaImageComponent, UnavailableComponent } from '@shared/components';
import { MediaSelectionService } from '@app/dialogs';
import { FormOptionsFacade } from '@app/store/form-options';
import { OrganisationFacade } from '@app/store/organisation';

@Component({
    selector: 'app-organisation-edit-modal',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.css'],
    imports: [ReactiveFormsModule, TranslateModule, ButtonModule, FormInputComponent, FormMultiSelectComponent, MediaImageComponent, UnavailableComponent],
})
export class EditComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly organisationFacade = inject(OrganisationFacade);
    private readonly formOptionsFacade = inject(FormOptionsFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly mediaSelectionService = inject(MediaSelectionService);

    readonly organisation = this.organisationFacade.organisation;
    readonly loading = this.organisationFacade.loading;
    readonly saving = this.organisationFacade.saving;
    readonly error = this.organisationFacade.error;

    readonly schoolOptions = this.formOptionsFacade.schools;

    readonly form = this.formBuilder.group({
        name: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        contactName: this.formBuilder.control('', [FormValidators.Text]),
        contactEmail: this.formBuilder.control('', [FormValidators.Email]),
        logoMediaId: this.formBuilder.control<string | null>(null),
        schoolIds: this.formBuilder.control<string[]>([]),
    });

    constructor() {
        if (this.formOptionsFacade.schoolsLoaded() !== true) {
            this.formOptionsFacade.getSchools();
        }

        const id = this.route.parent?.snapshot.params['id'];
        if (id) {
            this.organisationFacade.getOrganisation(id);
        }

        effect(() => {
            const organisation = this.organisation();
            if (organisation) {
                this.form.patchValue({
                    name: organisation.name,
                    contactName: organisation.contactName,
                    contactEmail: organisation.contactEmail,
                    logoMediaId: organisation.logoMediaId,
                    schoolIds: organisation.schoolIds,
                });
            }
        });

        effect(() => {
            if (this.organisationFacade.saved()) {
                this.close();
            }
        });

        effect(() => {
            if (this.organisationFacade.saving() === false && this.organisationFacade.saved() === false) {
                this.form.enable();
            }
        });

        effect(() => {
            const selection = this.mediaSelectionService.selected();
            if (!selection) return;

            if (selection.target === 'logoMediaId') {
                this.form.controls.logoMediaId.setValue(selection.media.id);
                this.mediaSelectionService.clear();
            }
        });
    }

    ngOnDestroy(): void {
        this.organisationFacade.clear();
    }

    onSelectLogo(): void {
        this.router.navigate([{ outlets: { dialog: ['select-media', { mediaTarget: 'logoMediaId', mediaType: 'image' }] } }]);
    }

    onClearLogo(): void {
        this.form.controls.logoMediaId.setValue(null);
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        const organisation = this.organisation();
        if (this.form.valid && organisation) {
            this.form.disable();
            this.organisationFacade.updateOrganisation(organisation.id, this.form.getRawValue());
        }
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }]);
    }
}
